import { useDate } from 'vuetify';
import { useI18n } from 'vue-i18n';

import { formatDate as customFormatDate } from '@/shared/services/date-helper';

export default function useDateFormatting() {
  const { locale } = useI18n();
  const vuetifyDate = useDate();

  const haitianCreoleFormatting = {
    long: [
      'janvye',
      'fevriye',
      'mas',
      'avil',
      'me',
      'jen',
      'jiyè',
      'out',
      'septanm',
      'oktòb',
      'novamn',
      'desamn',
    ],
  };

  /**
   * Format a date with various options
   * @param {string|Date} date - The date to format
   * @param {Object} options - Formatting options
   * @param {boolean} options.shortMonth - Use short month format
   * @param {boolean} options.hideDay - Hide the day
   * @param {boolean} options.hideYear - Hide the year
   * @param {string} options.timeZone - Timezone (defaults to 'UTC')
   * @returns {string} Formatted date string
   */
  const formatDate = (date, options = {}) => {
    if (!date) return '';

    const { shortMonth = false, hideDay = false, hideYear = false, timeZone = 'UTC' } = options;

    const dateObj = new Date(date);

    if (locale.value === 'ht') {
      const day = dateObj.getUTCDate();
      const month = haitianCreoleFormatting.long[dateObj.getUTCMonth()];
      const year = dateObj.getUTCFullYear();

      const parts = [];
      if (!hideDay) parts.push(day);
      parts.push(month);
      if (!hideYear) parts.push(year);

      return parts.join(' ');
    } else {
      const formatOptions = {
        month: shortMonth ? 'short' : 'long',
        timeZone,
      };

      if (!hideDay) formatOptions.day = 'numeric';
      if (!hideYear) formatOptions.year = 'numeric';

      return dateObj.toLocaleDateString(locale.value, formatOptions);
    }
  };

  /**
   * Format a date in short format (short month, day, year)
   * @param {string|Date} date - The date to format
   * @returns {string} Formatted date string
   */
  const formatShortDate = (date) => {
    return formatDate(date, {
      shortMonth: true,
      hideDay: false,
      hideYear: false,
    });
  };

  /**
   * Format a date in long format (long month, day, year)
   * @param {string|Date} date - The date to format
   * @returns {string} Formatted date string
   */
  const formatLongDate = (date) => {
    return formatDate(date, {
      shortMonth: false,
      hideDay: false,
      hideYear: false,
    });
  };

  /**
   * Care-as-of calendar: helpers for v-date-picker `allowed-dates` in the subsidy search flow.
   * v-date-picker may pass Date, YYYY-MM-DD, or an adapter-specific value.
   * Pass the object returned from Vuetify `useDate()` as `vuetifyDate`.
   */
  const toJsDateForAllowedDates = (value) => {
    if (value == null) {
      return null;
    }
    if (value instanceof Date) {
      return vuetifyDate.toJsDate(value);
    }
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return vuetifyDate.parseISO(value);
      }
      return new Date(value);
    }
    return vuetifyDate.toJsDate(value);
  };

  const allowedDates = (inputDate, now = new Date()) => {
    const asDate = toJsDateForAllowedDates(inputDate);
    if (asDate == null) {
      return true;
    }
    if (!vuetifyDate.isValid(asDate)) {
      return true;
    }
    const startOfInput = vuetifyDate.startOfDay(asDate);
    const startOfToday = vuetifyDate.startOfDay(now);
    return startOfInput.getTime() >= startOfToday.getTime();
  };

  return {
    allowedDates,
    customFormatDate,
    formatDate,
    formatShortDate,
    formatLongDate,
    toJsDateForAllowedDates,
  };
}
